<template>
  <div v-if="userHasPermission(constants.PERMISSION_TYPES.ADDRESS, constants.PERMISSION_VALUES.CREATE) && localIsVisible">
    <div :class="{'spl-modal-holder-show': localIsVisible}" class="spl-modal-holder spl-edit-create-address-modal">
      <div class="spl-modal">
        <h2 v-if="$mq === 'sm'">
          {{ (addressModel.Name_1 ? 'Edit' : 'Create') + ' Address' }}
        </h2>
        <div class="row">
          <div class="col12">
            <!-- *********************** Customer  *********************************** -->
            <form-label>Customer</form-label>
            <form-filtered-select
              :required="true"
              :options="customerOptions"
              :selected-key="selectedCustomer ? selectedCustomer.value : -1"
              :disabled="true"
              @updateDropdownValue="customer_Set" /><br>
          </div>
        </div>
        <!-- *********************** NAME  *********************************** -->
        <div class="row">
          <div class="col12">
            <form-label>Name</form-label>
          </div>
        </div>
        <div class="row">
          <div class="col9">
            <form-input
              v-model="address.Name_1"
              :required="true"
              :invalid="$v.address.Name_1.$error"
              data-cy="address-modal-name"
              placeholder="Name 1"
              @blur="$v.address.Name_1.$touch()" />
            <span v-if="$v.address.Name_1.$error" class="error">Name is required.</span><br>
          </div>
          <div class="col3">
            <form-button
              v-if="!show_full_name"
              wide="false"
              secondary="false"
              @onClick="show_More_Names">
              Show more
            </form-button>
            <form-button
              v-if="show_full_name"
              wide="false"
              secondary="false"
              @onClick="show_Less_Names">
              Show less
            </form-button>
          </div>
        </div>
        <div v-if="show_full_name" class="row">
          <div class="col12">
            <form-input v-model="address.Name_2" placeholder="Name 2" /><br>
            <form-input v-model="address.Name_3" placeholder="Name 3" /><br>
            <form-input v-model="address.Name_4" placeholder="Name 4" />
          </div>
        </div>

        <!-- *********************** STREET  *********************************** -->
        <div class="row">
          <div class="col12">
            <form-label>Street</form-label>
          </div>
        </div>
        <div class="row">
          <div class="col9">
            <form-input
              v-model="address.Street_1"
              :required="true"
              :invalid="$v.address.Street_1.$error"
              data-cy="address-modal-street"
              placeholder="Street 1"
              @blur="$v.address.Street_1.$touch()" />
            <span v-if="$v.address.Street_1.$error" class="error">Street is required.</span><br>
          </div>
          <div class="col3">
            <form-button
              v-if="!show_full_street"
              wide="false"
              secondary="false"
              @onClick="show_More_Streets">
              Show more
            </form-button>
            <form-button
              v-if="show_full_street"
              wide="false"
              secondary="false"
              @onClick="show_Less_Streets">
              Show less
            </form-button>
          </div>
        </div>
        <div v-if="show_full_street" class="row">
          <div class="col12">
            <form-input v-model="address.Street_2" placeholder="Street 2" /><br>
            <form-input v-model="address.Street_3" placeholder="Street 3" /><br>
            <form-input v-model="address.Street_4" placeholder="Street 4" />
          </div>
        </div>
        <!-- *********************** POSTAL CODE AND CITY  *********************************** -->
        <div class="row">
          <div class="col4">
            <form-label>Postal Code</form-label>
            <form-input
              v-model="address.Postal_Code"
              data-cy="address-modal-postal-code"
              :required="true"
              placeholder="Postal Code" />
          </div>
          <div class="col8">
            <form-label>City</form-label>
            <form-input
              v-model="address.City"
              :required="true"
              :invalid="$v.address.City.$error"
              data-cy="address-modal-postal-city"
              placeholder="City"
              @blur="$v.address.City.$touch()" />
            <span v-if="$v.address.City.$error" class="error">City is required.</span><br>
          </div>
        </div>

        <!-- *********************** Country  *********************************** -->
        <div class="row">
          <div class="col12">
            <form-label>Country</form-label>
            <country-select
              v-model="selectedCountry"
              :invalid="$v.selectedCountry.$error"
              data-cy="address-modal-postal-country"
              @close="$v.selectedCountry.$touch()" /><br>
            <span v-if="$v.selectedCountry.$error" class="error">Country is required.</span><br>
          </div>
        </div>
        <div class="row">
          <div class="col6">
            <form-button
              wide="true"
              secondary="true"
              @onClick="close">
              Cancel
            </form-button>
          </div>
          <div class="col6">
            <form-button
              wide="true"
              :disabled="$v.$invalid"
              data-cy="address-modal-save"
              @onClick="save">
              Save
            </form-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import webApi from '@/webApi'
import { CreateAddressModel } from '@/webApi/DataModels'
import Helpers from '@/helpers.vue'
import constants from '@/constants'
import { clone } from 'lodash'
import { mapGetters } from 'vuex'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'EditCreateAddressModal',
  mixins: [Helpers, validationMixin],
  props: {
    addressModel: {
      default: function () {
        return {}
      }
    },
    customerKey: {
      type: Number,
      default: -1
    },
    countryKey: {
      type: Number,
      default: -1
    },
    isVisible: {
      type: Boolean,
      default: false
    }
  },
  data () {
    // This is the empty address, we will use this if the user wants to create a new address from srcatch
    const addressTemplate = {
      Address_ID: -1,
      Active: true,
      Address_Reference: '',
      City: '',
      Country_ID: null,
      Country_Name: '',
      Customer_ID: null,
      Customer_Name: '',
      Name_1: '',
      Name_2: '',
      Name_3: '',
      Name_4: '',
      Postal_Code: '',
      Region: '',
      Street_1: '',
      Street_2: '',
      Street_3: '',
      Street_4: ''
    }
    return {
      localIsVisible: false,
      show_full_name: false,
      show_full_street: false,

      // We check if the user wanted to use an existing address as template or a use an empty template
      empty_address: addressTemplate,
      address: addressTemplate,
      selectedCustomer: null,
      selectedCountry: null,
      constants
    }
  },
  validations: {
    selectedCountry: {
      required
    },
    address: {
      Name_1: {
        required
      },
      Street_1: {
        required
      },
      City: {
        required
      }
    }
  },
  computed: {
    ...mapGetters('country', ['countryOptions']),
    ...mapGetters('customer', ['customerOptions'])
  },
  watch: {
    // Check if the property is changing
    isVisible: function (newValue) {
      this.localIsVisible = newValue
    },

    addressModel: {
      handler: function (newAddress, oldAddress) {
        this.addressModel = newAddress
        this.address = this.get_New_Address()
        this.$v.$reset()
      },
      deep: true,
      immediate: true
    },
    customerKey: function (newValue) {
      // Empty address Customer_ID is set to the selected Customer_ID since we want to force that value
      this.empty_address.Customer_ID = newValue
      this.set_Customer_Dropdown_By_ID(newValue)
    },
    countryKey: function (newValue) {
      this.empty_address.Country_ID = newValue
      this.set_Country_Dropdown_By_ID(newValue)
    },
    selectedCountry: function () {
      if (this.selectedCountry) {
        this.address.Country_ID = this.selectedCountry.value
        this.address.Country_Name = this.selectedCountry.label
      } else {
        this.address.Country_ID = -1
        this.address.Country_Name = ''
      }
    }
  },
  mounted () {
    this.address.Country_ID = this.countryKey || -1
    this.empty_address.Country_ID = this.countryKey || -1

    this.address.Customer_ID = this.customerKey || -1
    this.empty_address.Customer_ID = this.customerKey || -1

    if (this.address.Country_ID) {
      this.set_Country_Dropdown_By_ID(this.address.Country_ID)
    }

    if (this.address.Customer_ID) {
      this.set_Customer_Dropdown_By_ID(this.address.Customer_ID)
    }

    // Copy the passed addressModel and the empty_address to address
    this.address = this.get_New_Address()

    this.$v.$reset()
  },
  methods: {
    //* ************************************* Save new address ************************************/
    save () {
      // First validate the address
      if (this.$v.$invalid) {
        window.bus.$emit('openErrorModal', 'Please fill all required fields before saving.')
        return
      }

      // The address should be valid -> save to server
      webApi.address.create_Address(this.address)
        .then((response) => {
          this.address.Address_ID = response.data as any
          this.$emit('address_Created', this.address)
        }).catch((response) => {
          window.bus.$emit('openErrorModal', 'Error creating address: ' + response.data.ExceptionMessage)
        })
    },

    close () {
      this.$emit('close')
    },

    //* ************************************* Load & setup address creation ************************************/

    // We will copy the passed address to our empty address to make sure we have all properties.
    get_New_Address () {
      var tempAddress:CreateAddressModel = clone(this.empty_address) as CreateAddressModel
      if (this.addressModel) {
        tempAddress.Address_ID = -1 // Always new, it is not allowed to overwrite exising address
        tempAddress.Active = true // New address => active
        tempAddress.Address_Reference = ''
        if (this.addressModel.City) tempAddress.City = this.addressModel.City
        if (this.addressModel.Country_ID) {
          tempAddress.Country_ID = this.addressModel.Country_ID
        }
        if (this.addressModel.Country_Name) {
          tempAddress.Country_Name = this.addressModel.Country_Name
          // Always set by name. The address model passed normally do not have country id
          this.set_Country_Dropdown_By_Name(this.addressModel.Country_Name)
          if (this.selectedCountry) {
            tempAddress.Country_ID = this.selectedCountry.value
          }
        }
        if (this.addressModel.Customer_ID) tempAddress.Customer_ID = this.addressModel.Customer_ID
        if (this.addressModel.Customer_Name) tempAddress.Customer_Name = this.addressModel.Customer_Name
        if (this.addressModel.Name_1) tempAddress.Name_1 = this.addressModel.Name_1
        if (this.addressModel.Name_2) tempAddress.Name_2 = this.addressModel.Name_2
        if (this.addressModel.Name_3) tempAddress.Name_3 = this.addressModel.Name_3
        if (this.addressModel.Name_4) tempAddress.Name_4 = this.addressModel.Name_4
        if (this.addressModel.Postal_Code) tempAddress.Postal_Code = this.addressModel.Postal_Code
        tempAddress.Region = ''
        if (this.addressModel.Street_1) tempAddress.Street_1 = this.addressModel.Street_1
        if (this.addressModel.Street_2) tempAddress.Street_2 = this.addressModel.Street_2
        if (this.addressModel.Street_3) tempAddress.Street_3 = this.addressModel.Street_3
        if (this.addressModel.Street_4) tempAddress.Street_4 = this.addressModel.Street_4
      }
      return tempAddress
    },

    //* ************************************* User Events ************************************/
    // User has chosen a customer in the dropdown
    customer_Set (item) {
      this.address.Customer_ID = item.value
      this.address.Customer_Name = item.label
    },
    set_Customer_Dropdown_By_ID (customerID) {
      for (var i in this.customerOptions) {
        if (this.customerOptions[i].value === customerID) {
          this.selectedCustomer = this.customerOptions[i]
          this.customer_Set(this.customerOptions[i])
          return
        }
      }
    },

    set_Country_Dropdown_By_ID (countryID) {
      for (var i in this.countryOptions) {
        if (this.countryOptions[i].value === countryID) {
          this.selectedCountry = this.countryOptions[i]
          return
        }
      }
    },
    set_Country_Dropdown_By_Name (countryName) {
      for (var i in this.countryOptions) {
        if (this.countryOptions[i].label === countryName) {
          this.selectedCountry = this.countryOptions[i]
          return
        }
      }
    },

    // Show more Name fields
    show_More_Names () {
      this.show_full_name = true
    },
    // Show less Name fields
    show_Less_Names () {
      this.show_full_name = false
    },
    // Show more Street fields
    show_More_Streets () {
      this.show_full_street = true
    },
    // Show less Name fields
    show_Less_Streets () {
      this.show_full_street = false
    }

  }
}
</script>

<style lang="scss">
    @import '../../variables.scss';
  .spl-edit-create-address-modal {
    & .error {
      margin-top: -10px;
    }
  }
</style>
